import React, { useEffect, useState } from 'react';
import { Button, Modal } from "react-bootstrap";
import SpinnerButton from "../../SpinnerButton/SpinnerButton";
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import CreateNewPud from "../ModalCreatePud/ModalBodies/CreatePudBody/CreateNewPud";
import DetailsPud from "./ModalBodies/DetailsPudBody/DetailsPud";
import PudLogs from "./ModalBodies/DetailsPudBody/PudLogs";
import { useApiClient } from '../../../hooks/useApiClient';
import { TiassistoPudDetailsResponseDTO } from "../../../api/models/TiassistoModels/TiassistoPudDetailsResponseDTO";
import useTiassistoStore from '../../../store/TiassistoStore';
import toaster from '../../../utils/toaster';
import { useQueryClient, useQuery } from "@tanstack/react-query";

interface ModalPudProps {
  isOpen: boolean;
  onClose: () => void;
  mode: string;
  pudData?: TiassistoPudDetailsResponseDTO[];
  licensePlate?: string;
  from?: string
}

const ModalPud = ({ isOpen, onClose, mode, pudData, licensePlate = "", from }: ModalPudProps) => {
  const { t } = useTranslation();
  const { apiClient, handleApi } = useApiClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleteButtonEnabled, setIsDeleteButtonEnabled] = useState(true);
  const queryClient = useQueryClient();
  const { setPudId, pudId } = useTiassistoStore();

  const onDeletePud = () => {
    handleApi(apiClient.tiassistoController.deletePudRequest(pudId))
      .then(res => {
        toaster.success(i18next.t('pud_deleted_successfully'));
        queryClient.invalidateQueries({ queryKey: ["activePudList"] });
        setPudId("");
        onClose();
      })
      .catch(error => {
        console.error(error);
        toaster.error(i18next.t('error_delete_pud'));
      });
  };

  useEffect(() => {
    if (!isOpen) {
      setConfirmDelete(false);
    }
    return () => {
      queryClient.invalidateQueries({ queryKey: ["activePudList"] })
    }
  }, [isOpen]);


  const ModalTitle = () => {
    switch (mode) {
      case 'create_new_pud':
        return <Modal.Title>{t("create_new_pud")}</Modal.Title>;
      case 'details_pud':
        return <Modal.Title>{t("view_details_pud")}</Modal.Title>;
      default:
        return <></>;
    }
  };

  const ModalBody = () => {
    switch (mode) {
      case 'create_new_pud':
        return <CreateNewPud
          licensePlate={licensePlate}
          onClose={onClose}
        />;
      case "details_pud":
        return (
          <>
            <DetailsPud
              setIsDeleteButtonEnabled={setIsDeleteButtonEnabled}
              isOpen={isOpen}
              onClose={onClose}
            />
            <PudLogs
              isOpen={isOpen}
            />
          </>
        );
      default:
        return <div>{t("not_data_available")}</div>;
    }
  };

  const ModalFooter = () => {
    if (mode === 'details_pud') {
      return (
        <>
          {from !== 'historyPUD' && (
            confirmDelete ? (
              <>
                <div>{t('confirm_delete_permission')}</div>
                <Button variant="danger" onClick={onDeletePud}>
                  {i18next.t("yes")}
                </Button>
                <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                  {i18next.t("no")}
                </Button>
              </>
            ) : (
              <Button
                variant="danger"
                onClick={() => setConfirmDelete(true)}
                disabled={isDeleteButtonEnabled}
              >
                <SpinnerButton label={i18next.t("delete_process")} />
              </Button>
            )
          )}
          <Button onClick={onClose}>
            <SpinnerButton label={i18next.t("close")} />
          </Button>
        </>
      );
    }
    return null;
  };


  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <ModalTitle />
      </Modal.Header>
      <Modal.Body>
        <ModalBody />
      </Modal.Body>
      <Modal.Footer>
        <ModalFooter />
      </Modal.Footer>
    </Modal>
  );
};

export default ModalPud;
